import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

const Cart = () => {
  const { items, updateQty, removeItem, total } = useCart();

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="px-6 md:px-12 lg:px-20 py-32 text-center">
        <ShoppingBag size={36} strokeWidth={1} className="mx-auto mb-6 opacity-40" />
        <h1 className="font-display text-4xl mb-4">Your cart is empty</h1>
        <p className="opacity-70 mb-10">Begin with our apothecary or our clinical equipment.</p>
        <div className="flex gap-4 justify-center">
          <Link to="/shop/medicines" className="btn-primary">Browse Medicines</Link>
          <Link to="/shop/equipment" className="btn-secondary">Browse Equipment</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="px-6 md:px-12 lg:px-20 pt-24 pb-24">
      <p className="overline mb-4">Your Cart</p>
      <h1 className="font-display text-5xl mb-12">Review your selections</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="border-t" style={{ borderColor: "var(--ayu-border)" }}>
            {items.map((i) => (
              <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="grid grid-cols-12 gap-4 py-6 border-b items-center" style={{ borderColor: "var(--ayu-border)" }}>
                <img src={i.image} alt={i.name} className="col-span-3 sm:col-span-2 aspect-square object-cover" />
                <div className="col-span-9 sm:col-span-5">
                  <Link to={`/product/${i.slug}`} className="font-display text-xl block hover:underline">{i.name}</Link>
                  <p className="text-sm opacity-60 mt-1">₹{i.price.toLocaleString("en-IN")}</p>
                </div>
                <div className="col-span-6 sm:col-span-3 flex items-center border w-fit" style={{ borderColor: "var(--ayu-border)" }}>
                  <button data-testid={`decrease-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-3"><Minus size={14} /></button>
                  <span className="px-4 min-w-[40px] text-center">{i.quantity}</span>
                  <button data-testid={`increase-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-3"><Plus size={14} /></button>
                </div>
                <div className="col-span-6 sm:col-span-2 flex items-center justify-end gap-4">
                  <span className="text-sm">₹{(i.price * i.quantity).toLocaleString("en-IN")}</span>
                  <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i.product_id)} aria-label="Remove"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="p-8" style={{ background: "var(--ayu-surface)" }}>
            <p className="overline mb-4">Order Summary</p>
            <div className="flex justify-between mb-3 text-sm">
              <span>Subtotal</span><span>₹{total.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between mb-3 text-sm">
              <span>Shipping</span><span className="opacity-70">Calculated at next step</span>
            </div>
            <div className="border-t mt-6 pt-6 flex justify-between text-xl font-display" style={{ borderColor: "var(--ayu-border)" }}>
              <span>Total</span><span data-testid="cart-total">₹{total.toLocaleString("en-IN")}</span>
            </div>
            <Link to="/checkout" data-testid="proceed-to-checkout" className="btn-primary w-full justify-center mt-8">
              Proceed to Checkout
            </Link>
            <p className="text-xs opacity-60 mt-4 leading-relaxed">
              Checkout is enquiry-based. Our team will reach out within 24 hours to confirm the
              order and arrange shipping & payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
